import type { VenueVibe } from "@/lib/generated/stockFeedVibe";
import { stockVibeForVenue } from "@/lib/stockFeedVibe";

/** Controlled ambiance vocabulary (confirmed tags only reach consumers). */
export type AmbianceTag =
  | "romantic"
  | "cozy"
  | "energetic"
  | "elegant"
  | "casual"
  | "loud"
  | "intimate"
  | "trendy"
  | "hidden_gem";

const CALM_TAGS = new Set<string>(["cozy", "intimate", "romantic", "elegant"]);
const PARTY_TAGS = new Set<string>(["loud", "energetic"]);
const VIBRANT_TAGS = new Set<string>(["trendy"]);

/**
 * Derive a feed vibe bucket from confirmed ambiance tags.
 *
 * Priority on conflict: party > calm > vibrant.
 * `casual` / `hidden_gem` are neutral (no bucket) — caller falls back to stock.
 *
 * Keep in sync with `supabase/functions/_shared/venueVibe.ts`.
 */
export function vibeFromAmbianceTags(
  tags: readonly string[] | null | undefined,
): VenueVibe | undefined {
  if (!tags?.length) return undefined;
  const normalized = tags
    .filter((t): t is string => typeof t === "string")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  if (normalized.length === 0) return undefined;

  let hasParty = false;
  let hasCalm = false;
  let hasVibrant = false;
  for (const tag of normalized) {
    if (PARTY_TAGS.has(tag)) hasParty = true;
    else if (CALM_TAGS.has(tag)) hasCalm = true;
    else if (VIBRANT_TAGS.has(tag)) hasVibrant = true;
  }
  if (hasParty) return "party";
  if (hasCalm) return "calm";
  if (hasVibrant) return "vibrant";
  return undefined;
}

/**
 * Coverage-aware venue vibe: confirmed tags win when they map to a bucket;
 * otherwise fall back to the stock feed map (unchanged until coverage trigger).
 */
export function resolveVenueVibe(
  venueId: string,
  ambianceTags?: readonly string[] | null,
): VenueVibe | undefined {
  return vibeFromAmbianceTags(ambianceTags) ?? stockVibeForVenue(venueId);
}
